import { PermitType } from "common/types/permits";
import {
  PermitApplicationState,
  DocumentState,
  AuthorityState,
  PermitTypeSelectionState,
  VehicleSelectionState,
} from "./types";

// ============================================
// ACTION TYPES
// ============================================

export const SET_AUTHORITY = "SET_AUTHORITY" as const;
export const SET_PERMIT_TYPE = "SET_PERMIT_TYPE" as const;
export const SET_SELECTED_VEHICLES = "SET_SELECTED_VEHICLES" as const;
export const UPDATE_DOCUMENT = "UPDATE_DOCUMENT" as const;
export const CLEAR_DOCUMENT = "CLEAR_DOCUMENT" as const;
export const ACCEPT_TERMS = "ACCEPT_TERMS" as const;
export const CLEAR_TERMS = "CLEAR_TERMS" as const;
export const RESET_APPLICATION = "RESET_APPLICATION" as const;

// ============================================
// ACTION INTERFACES
// ============================================

interface SetAuthorityAction {
  type: typeof SET_AUTHORITY;
  payload: {
    authorityId: string | null;
    authorityName: string | null;
  };
}

interface SetPermitTypeAction {
  type: typeof SET_PERMIT_TYPE;
  payload: {
    permitTypeId: string;
    permitType: PermitType;
  };
}

interface SetSelectedVehiclesAction {
  type: typeof SET_SELECTED_VEHICLES;
  payload: {
    vehicleIds: string[];
    applyToAllVehicles: boolean;
    autoAssignNewVehicles: boolean;
  };
}

interface UpdateDocumentAction {
  type: typeof UPDATE_DOCUMENT;
  payload: {
    documentTypeId: string;
    documentId: string;
  };
}

interface ClearDocumentAction {
  type: typeof CLEAR_DOCUMENT;
  payload: {
    documentTypeId: string;
  };
}

interface AcceptTermsAction {
  type: typeof ACCEPT_TERMS;
}

interface ClearTermsAction {
  type: typeof CLEAR_TERMS;
}

interface ResetApplicationAction {
  type: typeof RESET_APPLICATION;
}

export type PermitApplicationAction =
  | SetAuthorityAction
  | SetPermitTypeAction
  | SetSelectedVehiclesAction
  | UpdateDocumentAction
  | ClearDocumentAction
  | AcceptTermsAction
  | ClearTermsAction
  | ResetApplicationAction;

// ============================================
// INITIAL STATE
// ============================================

export const INITIAL_AUTHORITY_STATE: AuthorityState = {
  authorityId: null,
  authorityName: null,
};

export const INITIAL_PERMIT_TYPE_SELECTION_STATE: PermitTypeSelectionState = {
  permitTypeId: null,
  permitType: null,
};

export const INITIAL_VEHICLE_SELECTION_STATE: VehicleSelectionState = {
  selectedVehicleIds: [],
  applyToAllVehicles: false,
  autoAssignNewVehicles: false,
};

export const INITIAL_STATE: PermitApplicationState = {
  authority: INITIAL_AUTHORITY_STATE,
  permitTypeSelection: INITIAL_PERMIT_TYPE_SELECTION_STATE,
  vehicleSelection: INITIAL_VEHICLE_SELECTION_STATE,
  documents: [],
  acceptedTermsAt: null,
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Creates documents array from permit type's required document types
 */
export function createDocumentsFromPermitType(permitType: PermitType): readonly DocumentState[] {
  if (!permitType.requiredDocumentTypes || permitType.requiredDocumentTypes.length === 0) {
    return [];
  }

  return permitType.requiredDocumentTypes.map((docType) => ({
    documentTypeId: docType.id,
    documentTypeName: docType.name,
    documentTypeDescription: docType.description ?? null,
    documentId: null,
  }));
}

// ============================================
// REDUCER
// ============================================

export function permitApplicationReducer(
  state: PermitApplicationState,
  action: PermitApplicationAction,
): PermitApplicationState {
  switch (action.type) {
    case SET_AUTHORITY: {
      // If same authority, no change needed
      if (state.authority.authorityId === action.payload.authorityId) {
        return state;
      }

      // Reset everything except authority when authority changes
      return {
        ...INITIAL_STATE,
        authority: {
          authorityId: action.payload.authorityId,
          authorityName: action.payload.authorityName,
        },
      };
    }

    case SET_PERMIT_TYPE: {
      // If same permit type, no change needed
      if (state.permitTypeSelection.permitTypeId === action.payload.permitTypeId) {
        return state;
      }

      // Keep authority, reset rest, init documents from permit type
      return {
        ...state,
        permitTypeSelection: {
          permitTypeId: action.payload.permitTypeId,
          permitType: action.payload.permitType,
        },
        vehicleSelection: INITIAL_VEHICLE_SELECTION_STATE,
        documents: createDocumentsFromPermitType(action.payload.permitType),
        acceptedTermsAt: null,
      };
    }

    case SET_SELECTED_VEHICLES: {
      return {
        ...state,
        vehicleSelection: {
          selectedVehicleIds: action.payload.vehicleIds,
          applyToAllVehicles: action.payload.applyToAllVehicles,
          autoAssignNewVehicles: action.payload.autoAssignNewVehicles,
        },
      };
    }

    case UPDATE_DOCUMENT: {
      const updatedDocuments = state.documents.map((doc) =>
        doc.documentTypeId === action.payload.documentTypeId ? { ...doc, documentId: action.payload.documentId } : doc,
      );

      return {
        ...state,
        documents: updatedDocuments,
      };
    }

    case CLEAR_DOCUMENT: {
      const updatedDocuments = state.documents.map((doc) =>
        doc.documentTypeId === action.payload.documentTypeId ? { ...doc, documentId: null } : doc,
      );

      return {
        ...state,
        documents: updatedDocuments,
      };
    }

    case ACCEPT_TERMS: {
      return {
        ...state,
        acceptedTermsAt: new Date().toISOString(),
      };
    }

    case CLEAR_TERMS: {
      return {
        ...state,
        acceptedTermsAt: null,
      };
    }

    case RESET_APPLICATION: {
      return INITIAL_STATE;
    }

    default:
      return state;
  }
}
